import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Lightning, FileArrowUp, Storefront, CreditCard, ShieldCheck, ArrowUpRight, HardDrives, Package } from "@phosphor-icons/react";
import { PublicNav } from "@/components/layout/PublicNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { NewsletterBox } from "@/components/growth";
import { AgentCard } from "@/components/agent/AgentCard";
import { api, startLogin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/lib/useMeta";

const HERO_IMG = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FEATURES = [
  { icon: Cpu, title: "Multi-modelo", text: "GPT-6, Claude 5.5 y Gemini 3.1 en un mismo lugar. Cambia de cerebro con un clic." },
  { icon: Lightning, title: "Skills modulares", text: "20 habilidades profesionales listas o crea las tuyas: ventas, soporte, legal, datos…" },
  { icon: FileArrowUp, title: "Entrenamiento real", text: "Sube PDF, DOCX, TXT, CSV, JSON y JSONL. Recuperación de conocimiento y ejemplos few-shot." },
  { icon: Storefront, title: "Marketplace", text: "Publica tu agente, fija precio único o suscripción y llega a empresas y personas." },
  { icon: CreditCard, title: "Cobros con Stripe", text: "Checkout seguro, impuestos gestionados y acceso inmediato tras el pago." },
  { icon: ShieldCheck, title: "Propiedad protegida", text: "Tus instrucciones y documentos nunca se exponen a los compradores." },
];

const STEPS = [
  { n: "01", t: "Diseña", d: "Describe tu idea y la IA genera el agente completo, o parte de una plantilla profesional." },
  { n: "02", t: "Entrena", d: "Añade skills, conocimiento de tu negocio y ejemplos de respuestas. Prueba en tiempo real." },
  { n: "03", t: "Vende", d: "Publica en el marketplace con tu precio. Cobra y escala sin infraestructura." },
];

export default function Landing() {
  const { user } = useAuth();
  const { data: meta } = useMeta();
  const { data: featured = [] } = useQuery({ queryKey: ["featured"], queryFn: async () => (await api.get("/marketplace?limit=6")).data });
  const { data: stats } = useQuery({ queryKey: ["mstats"], queryFn: async () => (await api.get("/marketplace/stats")).data });
  const cta = () => (user ? (window.location.href = "/agents/new") : startLogin("/agents/new"));

  return (
    <div className="noise min-h-screen bg-[#050505] text-white">
      <PublicNav />
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="grid-bg absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-7">
            <p className="eyebrow reveal mb-6">Plataforma de creación y venta de agentes IA</p>
            <h1 className="reveal font-display text-5xl font-extrabold leading-[0.92] tracking-tighter sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }}>
              Crea agentes de IA.<br /><span className="text-zinc-500">Véndelos</span> al mundo.
            </h1>
            <p className="reveal mt-8 max-w-xl text-lg leading-relaxed text-zinc-400" style={{ animationDelay: "160ms" }}>
              Crea agentes con el modelo exacto que necesitas, dótalos de skills, entrénalos con tu conocimiento y publícalos en un marketplace para empresas, negocios y personas.
            </p>
            <div className="reveal mt-10 flex flex-wrap gap-3" style={{ animationDelay: "240ms" }}>
              <button onClick={cta} className="btn-primary !px-7 !py-3.5 text-base" data-testid="hero-create-agent-button">
                Crear mi primer agente <ArrowRight size={18} weight="bold" />
              </button>
              <Link to="/marketplace" className="btn-ghost !px-7 !py-3.5 text-base" data-testid="hero-marketplace-button">Explorar marketplace</Link>
            </div>
            <div className="reveal mt-14 grid max-w-lg grid-cols-3 border border-white/10" style={{ animationDelay: "320ms" }}>
              {[["Agentes", stats?.agents ?? "—"], ["Modelos IA", stats?.models ?? "—"], ["Formatos", 11]].map(([l, v]) => (
                <div key={l} className="border-r border-white/10 p-4 last:border-r-0">
                  <p className="font-mono text-2xl font-semibold text-white">{v}</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-zinc-500">{l}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="reveal relative lg:col-span-5" style={{ animationDelay: "200ms" }}>
            <div className="relative h-full min-h-[380px] border border-white/10">
              <img src={HERO_IMG} alt="Red neuronal abstracta" className="absolute inset-0 h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/60 p-5 backdrop-blur-xl">
                <p className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">agent.compile()</p>
                <div className="mt-3 space-y-1.5 font-mono text-xs text-zinc-300">
                  <p><span className="text-zinc-500">model</span> → claude-sonnet-5-5</p>
                  <p><span className="text-zinc-500">skills</span> → [lead_qualifier, objection_handler]</p>
                  <p><span className="text-zinc-500">knowledge</span> → 3 docs · 412 fragmentos</p>
                  <p><span className="text-zinc-500">status</span> → <span className="text-emerald-400">publicado · 49,00 €</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <p className="eyebrow mb-4">Todo lo necesario</p>
          <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">Una fábrica completa, del prototipo a la venta.</h2>
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: I, title, text }, i) => (
              <div key={title} className="reveal group bg-[#070707] p-8 hover:bg-[#0e0e0e]" style={{ animationDelay: `${i * 60}ms`, transition: "background-color 200ms" }}>
                <I size={28} weight="duotone" className="text-blue-500" />
                <h3 className="mt-6 font-display text-xl font-bold">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="como-funciona" className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 lg:grid-cols-3">
          <div>
            <p className="eyebrow mb-4">Cómo funciona</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Tres pasos. Cero código.</h2>
          </div>
          <div className="lg:col-span-2">
            {STEPS.map((s) => (
              <div key={s.n} className="grid grid-cols-[80px_1fr] border-t border-white/10 py-8 last:border-b">
                <span className="font-mono text-sm text-blue-500">{s.n}</span>
                <div>
                  <h3 className="font-display text-2xl font-bold">{s.t}</h3>
                  <p className="mt-2 max-w-lg text-zinc-400">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="modelos" className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <p className="eyebrow mb-4">Modelos disponibles</p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Elige el cerebro de cada agente.</h2>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {(meta?.models || []).map((m) => (
              <div key={m.id} className="bg-[#070707] p-6" data-testid={`landing-model-${m.id}`}>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">{m.vendor} · {m.tier}</p>
                <p className="mt-3 font-display text-xl font-bold">{m.label}</p>
                <p className="mt-2 text-sm text-zinc-400">{m.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="mb-12 flex items-end justify-between">
            <div>
              <p className="eyebrow mb-4">Marketplace</p>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Agentes listos para trabajar.</h2>
            </div>
            <Link to="/marketplace" className="hidden items-center gap-1 text-sm font-semibold text-zinc-300 hover:text-white sm:inline-flex" data-testid="landing-see-all-link">
              Ver todos <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((a, i) => <AgentCard key={a.agent_id} agent={a} index={i} />)}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl gap-px px-6 py-24 md:grid-cols-2">
          {[
            ["/models", HardDrives, "Modelos locales", "IA personalizada que corre en tu propio equipo. Privacidad total, sin cuotas por uso.", "landing-models-teaser"],
            ["/software", Package, "Software HackLabs", "Programas profesionales con licencia única y descarga inmediata.", "landing-software-teaser"],
          ].map(([to, I, t, d, id]) => (
            <Link key={to} to={to} className="group relative overflow-hidden border border-white/10 bg-[#070707] p-10 hover:bg-[#0e0e0e]" data-testid={id} style={{ transition: "background-color 200ms" }}>
              <div className="grid-bg absolute inset-0 opacity-40" />
              <div className="relative">
                <I size={32} weight="duotone" className="text-cyan-400" />
                <h3 className="mt-6 font-display text-3xl font-bold tracking-tight">{t}</h3>
                <p className="mt-2 max-w-sm text-zinc-400">{d}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-white">Ver catálogo <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ transition: "transform 200ms" }} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="grid-bg absolute inset-0" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-24 md:flex-row md:items-end md:justify-between">
          <h2 className="max-w-3xl font-display text-4xl font-extrabold leading-none tracking-tighter sm:text-5xl lg:text-6xl">Tu conocimiento,<br />convertido en producto.</h2>
          <button onClick={cta} className="btn-primary !px-8 !py-4 text-base" data-testid="footer-cta-button">Empezar gratis <ArrowRight size={18} weight="bold" /></button>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-6 pb-24"><NewsletterBox source="landing" /></div>
      <SiteFooter />
    </div>
  );
}
